import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { getPlatformSettings, updatePlatformSettings, getAdminAuditLog } from '../utils/api';

export default function SuperAdminSettings() {
  const [form, setForm] = useState({
    platformName: '',
    supportEmail: '',
    companyName: '',
    defaultCurrency: 'INR',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [auditLog, setAuditLog] = useState([]);

  useEffect(() => {
    getPlatformSettings()
      .then((data) => setForm((f) => ({ ...f, ...data })))
      .catch(() => toast.error('Failed to load platform settings'))
      .finally(() => setLoading(false));

    getAdminAuditLog().then(setAuditLog).catch(() => setAuditLog([]));
  }, []);

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updatePlatformSettings(form);
      setForm((f) => ({ ...f, ...updated }));
      toast.success('Platform settings saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="fade-in">Loading...</div>;

  return (
    <div className="fade-in">
      <div className="page-header">
        <div className="page-title">Platform Settings</div>
        <div className="page-subtitle">
          Manage platform branding and defaults. Plan report/client limits are
          managed on the <a href="/super-admin/pricing">Pricing</a> page.
        </div>
      </div>

      <div className="card card-pad" style={{ maxWidth: 480 }}>
        <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 18 }}>
          Platform Branding
        </div>

        <div className="form-group">
          <label className="form-label">Platform Name</label>
          <input
            className="form-input"
            value={form.platformName}
            onChange={(e) => handleChange('platformName', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Company Name</label>
          <input
            className="form-input"
            value={form.companyName}
            onChange={(e) => handleChange('companyName', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Support Email</label>
          <input
            className="form-input"
            value={form.supportEmail}
            onChange={(e) => handleChange('supportEmail', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Default Currency</label>
          <select
            className="form-select"
            value={form.defaultCurrency}
            onChange={(e) => handleChange('defaultCurrency', e.target.value)}
          >
            <option value="INR">INR</option>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="GBP">GBP</option>
          </select>
        </div>

        <button
          className="btn btn-primary btn-lg"
          onClick={handleSave}
          disabled={saving}
          style={{ marginTop: 8 }}
        >
          {saving ? 'Saving...' : 'Save Platform Settings'}
        </button>
      </div>

      <div className="card card-pad" style={{ marginTop: 22 }}>
        <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 12 }}>
          Recent Admin Activity
        </div>
        {auditLog.length === 0 ? (
          <div style={{ color: 'var(--text2)', fontSize: 13 }}>No admin actions recorded yet.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Admin</th>
                  <th>Action</th>
                  <th>Target</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {auditLog.map((entry) => (
                  <tr key={entry.id}>
                    <td style={{ fontSize: 12 }}>{entry.admin_email}</td>
                    <td style={{ fontSize: 12, textTransform: 'capitalize' }}>{entry.action.replaceAll('_', ' ')}</td>
                    <td style={{ fontSize: 12 }}>{entry.target_type} {entry.target_id ? `#${entry.target_id}` : ''}</td>
                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>{new Date(entry.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
